import * as apiGetUser from 'api/user/GetUserIdV1';
import * as apiPostUser from 'api/user/PostUserCreateV1';
import * as apiPutUser from 'api/user/PutUserIdV1';
import * as apiGetUserProfile from 'api/userProfile/GetUserProfileV1';
import * as apiPutUserProfile from 'api/userProfile/PutUserProfileV1';
import * as Image from 'types/Media/Image';
import * as UserApiKey from 'types/User/Admin/UserApiKey/UserApiKey';
import { UserRole } from 'types/User/Admin/UserRole/UserRole';
import * as UserSettings from 'types/User/UserProfile/UserSettings';

export type User = {
	firstName: string;
	lastName: string;
	email: string | null;
	passwordCurrent: string | null;
	passwordNew1: string | null;
	passwordNew2: string | null;
	languageId: string;
	cultureCode: string | null;
	profilePicture: Image.Type | null;
	userSettings: UserSettings.Type | null;
	emailConfirmed: boolean;
	roles: UserRole[] | null;
	apiKeys: UserApiKey.Type[] | null;
	isActive: boolean | null;
	isExternallyAuthenticated: boolean | null;
	tenantName: string | null;
};

export const defaultUser = (): User => {
	return {
		firstName: '',
		lastName: '',
		email: null,
		passwordCurrent: null,
		passwordNew1: null,
		passwordNew2: null,
		languageId: '',
		cultureCode: null,
		profilePicture: null,
		userSettings: UserSettings.create(),
		emailConfirmed: false,
		roles: null,
		apiKeys: null,
		isActive: null,
		isExternallyAuthenticated: null,
		tenantName: '',
	};
};

export function mapUserFromApi(user: apiGetUser.ResponseData): User {
	return {
		firstName: user.firstName,
		lastName: user.lastName,
		email: user.email,
		passwordCurrent: null,
		passwordNew1: null,
		passwordNew2: null,
		languageId: user.languageId,
		cultureCode: null,
		profilePicture: user.profilePicture,
		userSettings: UserSettings.mapFromApi(user.userSettings),
		emailConfirmed: user.emailConfirmed,
		roles: user.roles,
		apiKeys: null,
		isActive: user.isActive,
		isExternallyAuthenticated: user.isExternallyAuthenticated,
		tenantName: null,
	};
}

export function mapUserToApi(user: User): apiPutUser.Request {
	return {
		firstName: user.firstName,
		lastName: user.lastName,
		email: user.email ?? '',
		languageId: user.languageId,
		profilePictureId: user.profilePicture?.id ? user.profilePicture.id : undefined,
		userSettings: UserSettings.mapToApi(user.userSettings),
		roles: user.roles?.map((e) => e.id) ?? [],
		isActive: user.isActive ?? true,
	};
}

export function mapNewUserToApi(user: User): apiPostUser.Request {
	return {
		user: {
			isActive: user.isActive ?? true,
			firstName: user.firstName,
			lastName: user.lastName,
			email: user.email ?? '',
			languageId: user.languageId,
			profilePictureId: user.profilePicture?.id ? user.profilePicture.id : undefined,
			userSettings: UserSettings.mapToApi(user.userSettings),
			roles: user.roles?.map((e) => e.id) ?? [],
		},
		confirmationPageUrl: window.origin + '/verify',
	};
}

export function mapUserProfileFromApi(user: apiGetUserProfile.ResponseData): User {
	return {
		firstName: user.firstName,
		lastName: user.lastName,
		email: null,
		passwordCurrent: null,
		passwordNew1: null,
		passwordNew2: null,
		languageId: user.languageId,
		cultureCode: null,
		profilePicture: user.profilePicture,
		userSettings: UserSettings.mapFromApi(user.userSettings),
		emailConfirmed: true,
		roles: null,
		apiKeys: UserApiKey.mapFromApi(user.apiKeys),
		isActive: null,
		isExternallyAuthenticated: user.isExternallyAuthenticated,
		tenantName: null,
	};
}

export function mapUserProfileToApi(user: User): apiPutUserProfile.Request {
	return {
		firstName: user.firstName,
		lastName: user.lastName,
		password: user.passwordCurrent ?? undefined,
		newPassword: user.passwordNew1 ?? undefined,
		languageId: user.languageId,
		profilePictureId: user.profilePicture?.id ? user.profilePicture.id : undefined,
		userSettings: UserSettings.mapToApi(user.userSettings),
	};
}
